import { Link, useSearchParams } from "react-router-dom";
import { EmptyState, PageHeader } from "@/components/Layout";
import { PHASE_LABEL, SessionStateLabel } from "@/components/session";
import { SessionTrade } from "@/components/trade";
import { ErrorNote, Field, inputClass, useCanWrite } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { useSession, useSessions } from "@/lib/queries";
import type { SessionListItem } from "@/lib/types";

const LIVE = ["ACTIVE", "PAUSED"];

/** La voce da cui si opera ogni giorno: scelta la sessione, tutto il resto è SessionTrade. */
export function Trade() {
  const canWrite = useCanWrite();
  const [params, setParams] = useSearchParams();
  const sessions = useSessions({ state: LIVE });
  const list = sessions.data ?? [];

  // Scelta dall'indirizzo; con una sola sessione viva è già quella.
  const requested = params.get("fase");
  const selectedId = list.find((s) => s.id === requested)?.id ?? (list.length === 1 ? list[0]!.id : "");

  const header = <PageHeader title="Operazione" subtitle="Direzione, piano e apertura della coppia sulla fase scelta" />;

  if (sessions.isLoading) {
    return (
      <>
        {header}
        <p className="px-6 py-6 text-sm text-muted">Caricamento…</p>
      </>
    );
  }
  if (sessions.error) {
    return (
      <>
        {header}
        <div className="px-6 py-6">
          <ErrorNote>{errorMessage(sessions.error)}</ErrorNote>
        </div>
      </>
    );
  }
  if (!list.length) {
    return (
      <>
        {header}
        <EmptyState
          title="Nessuna fase attiva"
          hint="Le operazioni si aprono dentro una fase: creane una scegliendo cliente, conti e parametri, poi torna qui per aprire la coppia."
          action={
            canWrite && (
              <Link to="/fasi/nuova" className="inline-flex rounded bg-ink px-3 py-1.5 text-sm font-medium text-page hover:bg-ink/90">
                Nuova fase
              </Link>
            )
          }
        />
      </>
    );
  }

  return (
    <>
      {header}
      <div className="space-y-5 px-6 py-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-full max-w-md">
            <Field label="Fase">
              {(id) => (
                <select
                  id={id}
                  value={selectedId}
                  onChange={(e) => setParams(e.target.value ? { fase: e.target.value } : {}, { replace: true })}
                  className={inputClass}
                >
                  {!selectedId && (
                    <option value="" disabled>
                      Scegli la fase su cui operare
                    </option>
                  )}
                  {list.map((s) => (
                    <option key={s.id} value={s.id}>
                      {optionLabel(s)}
                    </option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          {selectedId && (
            <Link to={`/fasi/${selectedId}`} className="pb-2 text-sm text-muted hover:text-ink hover:underline">
              Dettaglio della fase
            </Link>
          )}
        </div>

        {selectedId ? (
          <SelectedSession id={selectedId} canWrite={canWrite} />
        ) : (
          <p className="text-sm text-muted">Ci sono {list.length} fasi vive: scegli quella su cui aprire la coppia.</p>
        )}
      </div>
    </>
  );
}

function optionLabel(s: SessionListItem) {
  const pausa = s.state === "PAUSED" ? " · in pausa" : "";
  const aperta = s.summary.openPairs > 0 ? " · coppia aperta" : "";
  return `${s.cycle.client.name} · ${PHASE_LABEL(s.phase)} · ${s.cycle.preset.propFirm.name} ${s.cycle.preset.name}${pausa}${aperta}`;
}

function SelectedSession({ id, canWrite }: { id: string; canWrite: boolean }) {
  const session = useSession(id);
  if (session.isLoading) return <p className="text-sm text-muted">Caricamento della fase…</p>;
  if (session.error || !session.data) return <ErrorNote>{session.error ? errorMessage(session.error) : "Fase non trovata"}</ErrorNote>;

  const s = session.data;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
        <SessionStateLabel state={s.state} />
        <span>
          {s.symbols?.prop.brokerSymbol ?? s.canonicalSymbol} contro {s.symbols?.broker.brokerSymbol ?? s.canonicalSymbol} · massimo {s.maxOpsPerDay}{" "}
          {s.maxOpsPerDay === 1 ? "operazione" : "operazioni"} al giorno
        </span>
      </div>
      <SessionTrade s={s} canWrite={canWrite} />
    </div>
  );
}
